import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { competencyService, CompetencyMatrix, TrainingRecord, CertificationRecord } from '../services/competency.service';
import { message } from 'antd';
import { useAuthStore } from '@/store/authStore';

type Filters = Record<string, string | number | boolean | undefined | null>;

// Query keys
export const competencyKeys = {
  all: ['competency'] as const,
  employeeCompetencies: (employeeId: string) => [...competencyKeys.all, 'competencies', employeeId] as const,
  employeeTrainings: (employeeId: string) => [...competencyKeys.all, 'trainings', employeeId] as const,
  employeeCertifications: (employeeId: string) => [...competencyKeys.all, 'certifications', employeeId] as const,
  overview: (f?: Filters) => [...competencyKeys.all, 'overview', f] as const,
  myTeam: () => [...competencyKeys.all, 'my-team'] as const,
  employeeFile: (employeeId: string) => [...competencyKeys.all, 'file', employeeId] as const,
  jobProfiles: (f?: Filters) => [...competencyKeys.all, 'job-profiles', f] as const,
  jobProfile: (id: string) => [...competencyKeys.all, 'job-profile', id] as const,
  courses: (f?: Filters) => [...competencyKeys.all, 'courses', f] as const,
  schedule: (year: number, f?: Filters) => [...competencyKeys.all, 'schedule', year, f] as const,
  sessions: (f?: Filters) => [...competencyKeys.all, 'sessions', f] as const,
  session: (id: string) => [...competencyKeys.all, 'session', id] as const,
  effectivenessList: (f?: Filters) => [...competencyKeys.all, 'effectiveness', f] as const,
  effectiveness: (id: string) => [...competencyKeys.all, 'effectiveness-detail', id] as const,
  assessments: (f?: Filters) => [...competencyKeys.all, 'assessments', f] as const,
  assessment: (id: string) => [...competencyKeys.all, 'assessment', id] as const,
  cycles: () => [...competencyKeys.all, 'cycles'] as const,
  gapMatrix: (f?: Filters) => [...competencyKeys.all, 'gap-matrix', f] as const,
};

export const COMPETENCY_PERMS = {
  view: 'competency.view',
  create: 'competency.create',
  trainingView: 'competency.training.view',
  trainingCreate: 'competency.training.create',
  trainingManage: 'competency.training.manage',
  certificationCreate: 'competency.certification.create',
  jobProfileManage: 'competency.job_profile.manage',
  effectivenessEvaluate: 'competency.effectiveness.evaluate',
  assessmentView: 'competency.assessment.view',
  assessmentEvaluate: 'competency.assessment.evaluate',
  assessmentClose: 'competency.assessment.close',
  import: 'competency.import',
} as const;

/** Codes that open the company-wide employee lists (mirrors the server VIEW_ANY gate). */
export const COMPETENCY_VIEW_ANY = [
  COMPETENCY_PERMS.view,
  COMPETENCY_PERMS.trainingView,
  COMPETENCY_PERMS.assessmentView,
  COMPETENCY_PERMS.assessmentEvaluate,
  COMPETENCY_PERMS.assessmentClose,
  COMPETENCY_PERMS.trainingManage,
  COMPETENCY_PERMS.jobProfileManage,
];

/** True when the user holds any of the codes (super admin always). */
export function useCompetencyCan(...codes: string[]): boolean {
  const user = useAuthStore((s) => s.user);
  if (user?.is_super_admin) return true;
  const perms = user?.permissions ?? [];
  return codes.some((c) => perms.includes(c));
}

const live = { staleTime: 30_000 };

export const useCompetencyOverview = (f?: Filters, enabled = true) =>
  useQuery({ queryKey: competencyKeys.overview(f), queryFn: () => competencyService.overview(f), enabled, ...live });
export const useMyTeam = (enabled = true) =>
  useQuery({ queryKey: competencyKeys.myTeam(), queryFn: () => competencyService.myTeam(), enabled, staleTime: 300_000 });
export const useCompetencyEmployeeFile = (employeeId?: string) =>
  useQuery({
    queryKey: competencyKeys.employeeFile(employeeId ?? ''),
    queryFn: () => competencyService.employeeFile(employeeId as string),
    enabled: !!employeeId,
    ...live,
  });
export const useJobProfiles = (f?: Filters) =>
  useQuery({ queryKey: competencyKeys.jobProfiles(f), queryFn: () => competencyService.listJobProfiles(f), ...live });
export const useJobProfile = (id?: string) =>
  useQuery({ queryKey: competencyKeys.jobProfile(id ?? ''), queryFn: () => competencyService.getJobProfile(id as string), enabled: !!id, ...live });
export const useTrainingCourses = (f?: Filters, enabled = true) =>
  useQuery({ queryKey: competencyKeys.courses(f), queryFn: () => competencyService.listCourses(f), enabled, ...live });
export const useTrainingSchedule = (year: number, f?: Filters) =>
  useQuery({ queryKey: competencyKeys.schedule(year, f), queryFn: () => competencyService.getSchedule(year, f), ...live });
export const useTrainingSessions = (f?: Filters) =>
  useQuery({ queryKey: competencyKeys.sessions(f), queryFn: () => competencyService.listSessions(f), ...live });
export const useTrainingSession = (id?: string) =>
  useQuery({ queryKey: competencyKeys.session(id ?? ''), queryFn: () => competencyService.getSession(id as string), enabled: !!id, ...live });
export const useEffectivenessList = (f?: Filters, enabled = true) =>
  useQuery({ queryKey: competencyKeys.effectivenessList(f), queryFn: () => competencyService.listEffectiveness(f), enabled, ...live });
export const useEffectiveness = (id?: string) =>
  useQuery({ queryKey: competencyKeys.effectiveness(id ?? ''), queryFn: () => competencyService.getEffectiveness(id as string), enabled: !!id, ...live });
export const useCompetencyAssessments = (f?: Filters) =>
  useQuery({ queryKey: competencyKeys.assessments(f), queryFn: () => competencyService.listAssessments(f), ...live });
export const useCompetencyAssessment = (id?: string) =>
  useQuery({ queryKey: competencyKeys.assessment(id ?? ''), queryFn: () => competencyService.getAssessment(id as string), enabled: !!id, ...live });
export const useAssessmentCycles = (enabled = true) =>
  useQuery({ queryKey: competencyKeys.cycles(), queryFn: () => competencyService.listCycles(), enabled, ...live });
export const useGapMatrix = (f?: Filters, enabled = true) =>
  useQuery({ queryKey: competencyKeys.gapMatrix(f), queryFn: () => competencyService.gapMatrix(f), enabled, ...live });

export function apiErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  const e = error as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
  return e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || fallback;
}

/** Mutation that refreshes every competency query and reports the outcome. */
export function useCompetencyAction<TVars = void, TResult = unknown>(fn: (vars: TVars) => Promise<TResult>, successMessage?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: competencyKeys.all });
      if (successMessage) message.success(successMessage);
    },
    onError: (error) => message.error(apiErrorMessage(error)),
  });
}

export function useEmployeeCompetencies(employeeId: string) {
  return useQuery({
    queryKey: competencyKeys.employeeCompetencies(employeeId),
    queryFn: () => competencyService.getEmployeeCompetencies(employeeId),
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpsertCompetency() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CompetencyMatrix>) => competencyService.upsertCompetency(data),
    onSuccess: (_, variables) => {
      if (variables.employee_id) {
        queryClient.invalidateQueries({ queryKey: competencyKeys.employeeCompetencies(variables.employee_id) });
      }
      message.success('Competency saved successfully');
    },
    onError: () => message.error('Failed to save competency'),
  });
}

export function useEmployeeTrainings(employeeId: string) {
  return useQuery({
    queryKey: competencyKeys.employeeTrainings(employeeId),
    queryFn: () => competencyService.getEmployeeTrainings(employeeId),
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateTraining() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<TrainingRecord>) => competencyService.createTraining(data),
    onSuccess: (_, variables) => {
      if (variables.employee_id) {
        queryClient.invalidateQueries({ queryKey: competencyKeys.employeeTrainings(variables.employee_id) });
      }
      message.success('Training record created successfully');
    },
    onError: () => message.error('Failed to create training record'),
  });
}

export function useEmployeeCertifications(employeeId: string) {
  return useQuery({
    queryKey: competencyKeys.employeeCertifications(employeeId),
    queryFn: () => competencyService.getEmployeeCertifications(employeeId),
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateCertification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CertificationRecord>) => competencyService.createCertification(data),
    onSuccess: (_, variables) => {
      if (variables.employee_id) {
        queryClient.invalidateQueries({ queryKey: competencyKeys.employeeCertifications(variables.employee_id) });
      }
      message.success('Certification record created successfully');
    },
    onError: () => message.error('Failed to create certification record'),
  });
}

